import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import useSWR from 'swr'
import { useRouter } from 'next/router'
import {
  countBy,
  difference,
  get,
  intersectionWith,
  isEqual,
  omit,
  pick,
  uniq,
} from 'lodash'
import hash from 'object-hash'
import { SS_KEY_TABLE_CONFIGS, useSessionStorage } from '../useSessionStorage'
import { paginationInfo } from './tablePagination'
import { TableDrawerName } from 'ui/components/CbTableV2'
import {
  CacheKeyGenerator,
  Column,
  ResultsPromiseFromCacheKey,
  SortDirection,
  TableConfig,
  TableName,
} from './types'
import { downloadCSV } from 'ui/helpers/files'
import { pluralize } from 'inflection'
import { AppContext } from 'providers'
import { QueryParamDrawer } from 'types'

export const useTable = ({
  additionalFilters = [],
  columnDefs,
  configOverride,
  defaultConfig,
  exportModal,
  getCacheKey,
  getCSVData,
  getResults,
  getSelectedRowValue,
  hasBulkSelect = false,
  label = '',
  tableName,
  hideExport = false,
  emptyState,
}: {
  additionalFilters: any[]
  columnDefs: Column[]
  configOverride?: TableConfig
  defaultConfig: TableConfig
  exportModal?: FC<any>
  getCacheKey: CacheKeyGenerator
  getCSVData: any
  getResults: ResultsPromiseFromCacheKey
  getSelectedRowValue?: (row) => any
  hasBulkSelect?: boolean
  label?: string
  tableName: TableName
  hideExport?: boolean
  emptyState: ReactNode | null
}) => {
  const { getJSON: getTableConfigs, setJSON: saveTableConfigs } =
    useSessionStorage(SS_KEY_TABLE_CONFIGS, {})

  const [openDrawerName, setOpenDrawerName] = useState<TableDrawerName>()
  const [openDrawerProps, setOpenDrawerProps] = useState<Record<string, any>>(
    {},
  )
  const [selectedRows, setSelectedRows] = useState<any>({})

  const [config, setConfig] = useState<TableConfig>(hydrateTableConfig)
  const { visibleColumns } = config

  const { setToast } = useContext(AppContext)

  const router = useRouter()
  const url = new URL(window.location.href)
  const drawerNameFromQuery = url.searchParams.get('drawer') as
    | TableDrawerName
    | QueryParamDrawer
    | null

  useEffect(() => {
    if (!drawerNameFromQuery) return

    /*
      This is required to allow the user to update their profile settings and password
      From any of the manage sections.
    */
    if (
      [
        QueryParamDrawer.ManageSettings,
        QueryParamDrawer.UpdatePassword,
      ].includes(drawerNameFromQuery as QueryParamDrawer)
    )
      return

    setOpenDrawerName(drawerNameFromQuery as TableDrawerName)

    url.searchParams.delete('drawer')
    router.replace(url.href, undefined, { shallow: true })
  }, [drawerNameFromQuery])

  // Close table drawers when the embedded parent (Upduo) navigates to a path
  // without a ?drawer param. Without this, switching from "Buy Tokens"
  // (which opens a drawer via ?drawer=BuyTokens) to "Transactions" (same base
  // URL, no param) leaves the drawer open because router.push to the same
  // pathname is a no-op and the useEffect above never fires.
  useEffect(() => {
    const handler = (e: Event) => {
      const { path } = (e as CustomEvent<{ path: string }>).detail
      const navUrl = new URL(path, window.location.origin)
      if (!navUrl.searchParams.get('drawer')) {
        setOpenDrawerName(undefined)
        setOpenDrawerProps({})
      }
    }
    window.addEventListener('upduo-navigate', handler)
    return () => window.removeEventListener('upduo-navigate', handler)
  }, [])

  useEffect(() => {
    const allConfigs = { ...getTableConfigs(), [tableName]: config }

    saveTableConfigs(allConfigs)
  }, [config])

  useEffect(() => {
    /* Some tables columns may dynamically change at runtime and are passed through
       via columnDefs + visible cols. via defaultConfig.
       Update the already-rendered table with the new state of if the columnIds have changed:
       Set visible columns to the current defaultConfig.visibleColumns (which may have some of the new ids) +
       any currently visible columns in case the user made a manual change from the first render
    */
    setVisibleColumns(
      uniq([...visibleColumns, ...defaultConfig.visibleColumns]),
    )
  }, [hashedColumnIds()])

  const {
    data,
    error,
    isValidating: loading,
    mutate: refetchTable,
  } = useSWR(getCacheKey(config, columnDefs), getResults)

  const toggleSelectedRow = row => {
    const key = getRowId(row)

    if (selectedRows[key]) {
      const currentlySelectedRows = { ...selectedRows }
      delete currentlySelectedRows[key]
      setSelectedRows(currentlySelectedRows)
    } else {
      setSelectedRows({
        ...selectedRows,
        [key]: rowValue(row),
      })
    }
  }

  /*
    In some actions, such as locking players, the selected players will be removed from the table
    after the action has succeeded. This means that the manager will not be able to manually deselect
    the selected rows so we will do it for them.
  */
  const deselectAllRows = () => {
    setSelectedRows({})
  }

  const rowValue = row => {
    if (getSelectedRowValue) {
      return getSelectedRowValue(row)
    } else {
      return true
    }
  }

  const allVisibleRowsSelected = () => {
    if (!data) return false

    return (
      data.results.filter(row => selectedRows[getRowId(row)]).length ==
      data.results.length
    )
  }

  const toggleSelectAllVisibleRows = () => {
    if (allVisibleRowsSelected()) {
      deselectAllVisibleRows()
    } else {
      selectAllVisibleRows()
    }
  }

  const selectAllVisibleRows = () => {
    const updatedRows = {}

    data?.results.forEach(row => (updatedRows[getRowId(row)] = rowValue(row)))

    setSelectedRows({ ...selectedRows, ...updatedRows })
  }

  const deselectAllVisibleRows = () => {
    const currentlySelectedRows = { ...selectedRows }

    data?.results.forEach(row => {
      delete currentlySelectedRows[getRowId(row)]
    })

    setSelectedRows(currentlySelectedRows)
  }

  const getRowId = row => row.id || hash(row)

  const getRowKey = row => `${tableName}-row-for-id-${getRowId(row)}`

  const setVisibleColumns = (newColumnIds: string[]) => {
    const removedColumnIds = difference(visibleColumns, newColumnIds)
    const newFilters = omit(config.filters, removedColumnIds)

    const newSearch = newColumnIds.includes(get(config, 'search.columnId', ''))
      ? config.search
      : {}

    const newSort = newColumnIds.includes(get(config, 'sort.columnId', ''))
      ? config.sort
      : {}

    setConfig(config => ({
      ...config,
      page: 0,
      filters: newFilters,
      search: newSearch,
      sort: newSort,
      visibleColumns: newColumnIds,
    }))
  }

  const columnInfo = {
    allColumns: columnDefs.map(col => pick(col, ['id', 'name', 'primary'])),
    setVisibleColumns,
    visibleColumns,
  }

  const columnsToDisplay: Column[] = useMemo(
    () =>
      intersectionWith(
        columnDefs,
        visibleColumns,
        (all, visibleId) => all.id === visibleId,
      ),
    [columnDefs, visibleColumns],
  )

  const headerInfo = columnsToDisplay.map(col => {
    const sortDirection = get(
      config,
      'sort.direction',
      SortDirection.None,
    ) as SortDirection
    const sortColumnId = get(config, 'sort.columnId', '')

    return {
      disableSort: !!col.disableSort,
      id: col.id,
      name: col.name,
      renderTooltipContent: col.renderTooltipContent,
      sortDirection:
        col.id === sortColumnId ? sortDirection : SortDirection.None,
    }
  })

  const searchInfo = columnsToDisplay
    .filter(col => col.searchable)
    .map(col => pick(col, ['id', 'name', 'searchLabel']))

  const bodyInfo = columnsToDisplay.map(col =>
    pick(col, [
      'cellAlignment',
      'id',
      'renderCell',
      'value',
      'valueFormatter',
      'wrapText',
    ]),
  )

  const filterInfo = columnsToDisplay
    .filter(col => col.filterable)
    .concat(additionalFilters)
    .map(col => ({
      columnId: col.id,
      columnName: col.name,
      excludeDrawerFilter: col.excludeDrawerFilter,
      filterConfig: col.filterConfig,
      quickFilterable: col.quickFilterable,
      quickFilterTooltipContent: col.quickFilterTooltipContent,
      value: get(config, `filters.${col.id}`, NO_FILTER),
    }))

  const drawerFilterInfo = filterInfo.filter(col => !col.excludeDrawerFilter)
  const quickFilterInfo = filterInfo.filter(col => col.quickFilterable)

  const clearFilterValues = drawerFilterInfo.reduce((acc, filter) => {
    const resetValue = get(filter, 'filterConfig.resetValue')
    if (resetValue === undefined) return acc

    return { ...acc, [filter.columnId]: resetValue }
  }, {})

  // isEqual will cover us when comparing two empty arrays, etc.
  const filterSummaryInfo = (() => {
    const numApplicableFilters =
      countBy(filterInfo, ({ quickFilterable, filterConfig, value }) =>
        quickFilterable ||
        value === NO_FILTER ||
        isEqual(filterConfig?.resetValue, value)
          ? 'notApplicable'
          : 'applicable',
      ).applicable || 0

    return { hasQuickFilters: quickFilterInfo.length > 0, numApplicableFilters }
  })()

  const updateFilter = ({
    columnId,
    value,
  }: {
    columnId: string
    value: string | string[] | null
  }) => {
    setConfig(config => ({
      ...config,
      page: 0,
      filters: { ...config.filters, [columnId]: value },
    }))
  }

  const handleColumnSort = (columnId: string) => {
    const sortColumnId = get(config, 'sort.columnId')
    const sortDirection = (
      sortColumnId === columnId
        ? get(config, 'sort.direction', SortDirection.None)
        : SortDirection.None
    ) as SortDirection

    const nextDirection = NEXT_DIRECTION[sortDirection]
    const sortConfig =
      nextDirection === SortDirection.None
        ? {}
        : { sort: { columnId, direction: nextDirection } }

    setConfig(config => {
      const { sort, ...restOfConfig } = config

      return {
        ...restOfConfig,
        page: 0,
        ...sortConfig,
      }
    })
  }

  const handleSearch = useCallback(
    ({ columnId, text }: { columnId: string; text: string }) =>
      setConfig(config => ({
        ...config,
        page: 0,
        search: { columnId, text },
      })),
    [],
  )

  const setDrawer = ({
    name,
    props = {},
  }: {
    name: TableDrawerName
    props?: Record<string, any>
  }) => {
    setOpenDrawerName(name)
    setOpenDrawerProps(props)
  }

  const closeDrawer = () => {
    setOpenDrawerName(undefined)
    setOpenDrawerProps({})
  }

  const handleExport = () =>
    getCSVData(config, columnDefs).then(response => {
      if (response?.data?.message == 'report_emailed') {
        setToast({
          title: 'Report Emailed',
          description:
            'We have queued your report for generation and will email it to you shortly',
          status: 'info',
          id: 'table-csv-async',
        })
      } else {
        downloadCSV(response, `${pluralize(label)}.csv`)
      }
    })

  if (error) {
    setToast({
      title: error.message,
      description: 'There was an error fetching data. Please try again.',
      status: 'error',
      id: 'table-load-error',
    })
  }

  return {
    allVisibleRowsSelected,
    bodyInfo,
    clearFilterValues,
    clearSavedConfig,
    closeDrawer,
    columnInfo,
    currentConfig: Object.freeze(config),
    deselectAllRows,
    defaultConfig,
    drawerFilterInfo,
    error,
    exportModal,
    filterInfo,
    filterSummaryInfo,
    getRowId,
    getRowKey,
    handleExport,
    handleColumnSort,
    handleSearch,
    hasBulkSelect,
    headerInfo,
    loading,
    openDrawerName,
    openDrawerProps,
    paginationInfo: paginationInfo({ data, config, setConfig, label }),
    quickFilterInfo,
    refetchTable,
    rows: data ? data.results : [],
    searchInfo,
    toggleSelectAllVisibleRows,
    selectedRows,
    setDrawer,
    tableName,
    toggleSelectedRow,
    updateFilter,
    hideExport,
    emptyState,
  }

  function hydrateTableConfig() {
    const COLUMN_ID_HASH_KEY = 'columnIdsHash'

    /* Don't use the saved config if columnIds have changed.  This will help
       ensure new columns appear even if a user has a long-lived session */
    const savedConfig = getTableConfigs()[tableName]
    const savedConfigHash = get(savedConfig, COLUMN_ID_HASH_KEY)

    const savedConfigIfCurrent =
      savedConfigHash === hashedColumnIds() ? savedConfig : {}

    // Merge saved config into default for backwards compatibility, malformation, etc.
    return {
      ...hookDefaults,
      ...defaultConfig,
      [COLUMN_ID_HASH_KEY]: hashedColumnIds(),
      ...savedConfigIfCurrent,
      /*
        In some cases we want to allow the mounting component to override the saved config. For example the manage metrics
        component redirects to the manage activities view and puts a metric ID in the URL params.
        We then want to set that metric ID as the metric filter even if one is already saved
      */
      ...configOverride,
    }
  }
  /* If we want to ignore a saved config on ANY/other table change, we could
     just modify this formula to account for more fields, etc. */
  function hashedColumnIds() {
    return hash(
      columnDefs.map(col => col.id),
      { unorderedArrays: true },
    )
  }

  function clearSavedConfig() {
    saveTableConfigs(omit(getTableConfigs(), tableName))
    setConfig(hydrateTableConfig())
  }
}

export const hookDefaults = {
  page: 0,
  perPage: 25,
  search: {},
}

const NEXT_DIRECTION = {
  [SortDirection.Asc]: SortDirection.Desc,
  [SortDirection.Desc]: SortDirection.None,
  [SortDirection.None]: SortDirection.Asc,
}

export const NO_FILTER = 'NO_FILTER'
